import {useContext} from 'react'

import {
  ChangeableFieldContext,
  ChangeableFieldContextProps
} from '../context/ChangeableFieldsContext'

/**
 * Mode for selecting which level of changeable fields to return
 * - 'base': Configuration + time-based rules only
 * - 'orderChangeRulesOnly': + Order Change rules (spacing, material, order confirmation)
 * - 'final': + Slot management rules (default)
 */
export type ChangeableFieldsMode = 'base' | 'orderChangeRulesOnly' | 'final'

/**
 * Safe check for whether the current tree is rendered inside an order-change flow.
 *
 * Unlike {@link useChangeableFields}, this does not throw when the ChangeableFieldContext
 * is absent (i.e. during order intake), so shared components/hooks can branch on the
 * process type without crashing the intake flow.
 */
export const useIsOrderChange = (): boolean => useContext(ChangeableFieldContext) !== undefined

/**
 * Hook to access changeable fields at different rule application levels.
 *
 * @param mode - Which level of rules to return (default: 'final')
 * @returns Changeable fields context with selected level
 */
export const useChangeableFields = (mode: ChangeableFieldsMode = 'final') => {
  const value = useContext(ChangeableFieldContext)

  if (!value) {
    throw new Error('useChangeableFields must be used within ChangeableFieldContext')
  }

  const changeableFields = (() => {
    switch (mode) {
      case 'base':
        return value.baseChangeableFields
      case 'orderChangeRulesOnly':
        return value.orderChangeRulesOnlyChangeableFields
      default:
        return value.changeableFields
    }
  })()

  return {
    changeableFields,
    confirmationState: calculateConfirmationStateForFields(changeableFields)
  }
}

// Helper to calculate confirmation state for specific fields
function calculateConfirmationStateForFields(
  fields: ChangeableFieldContextProps['changeableFields']
): ChangeableFieldContextProps['confirmationState'] {
  return fields.filter((x) => x.isDirty).some((x) => x.state === 'requireDispatchConfirmation')
    ? 'requireDispatchConfirmation'
    : 'autoConfirmable'
}
